import { Star } from "lucide-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { InventoryValueEntry, Requisition as ApiRequisition, useGetV4WarehousesQuery, Warehouse } from "randmar-api-client";
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import { Requisition as LegacyRequisition } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import LoadingComponent from "../util/LoadingComponent";

interface IProps {
  inventoryValues: InventoryValueEntry[];
  requisitions: (LegacyRequisition | ApiRequisition)[];
}

const isLegacy = (r: LegacyRequisition | ApiRequisition): r is LegacyRequisition =>
  Boolean(r) && "location" in r;

const VendorValues: React.FC<IProps> = ({ inventoryValues, requisitions: rawRequisitions }) => {
  const { data: warehousesData, isLoading } = useGetV4WarehousesQuery();
  const warehouses = (warehousesData as Warehouse[]) ?? [];

  const requisitions = React.useMemo(() => rawRequisitions.map(r =>
    isLegacy(r)
      ? {
        Location: r.location,
        SubTotal: r.subTotal
      }
      : r
  ), [rawRequisitions]);

  const warehouseDisplayData = React.useMemo(() => warehouses.map(w => {
    const inventoryForWarehouse = inventoryValues.filter(iv => iv.Location === w.WarehouseId);
    const requisitionsForWarehouse = requisitions.filter(po => po.Location === w.WarehouseId);

    const inventoryValue = inventoryForWarehouse.reduce((sum, value) => sum + (value.InventoryValue || 0), 0);
    const purchaseOrderValue = inventoryForWarehouse.reduce((sum, value) => sum + (value.PurchaseOrderValue || 0), 0);
    const salesOrderValue = inventoryForWarehouse.reduce((sum, value) => sum + (value.SalesOrderValue || 0), 0);
    const draftOrdersValue = requisitionsForWarehouse.reduce((sum, value) => sum + (value.SubTotal || 0), 0);

    const shouldDisplay = (inventoryValue + purchaseOrderValue + salesOrderValue > 0) || (draftOrdersValue > 0) || ['MTL', 'EDM'].includes(w.WarehouseId || "");
    const isPrimary = ['MTL', 'EDM'].includes(w.WarehouseId || "");

    return {
      warehouse: w,
      inventoryValue,
      draftOrdersValue,
      purchaseOrderValue,
      salesOrderValue,
      shouldDisplay,
      isPrimary
    };
  }).filter(d => d.shouldDisplay), [warehouses, inventoryValues, requisitions]);

  const backOrderLocations = ['GHOST', '3PLW', '3PLE'];
  const backOrdersData = React.useMemo(() => {
    const inventoryForBackOrders = inventoryValues.filter(iv => backOrderLocations.includes(iv.Location || ""));
    const requisitionsForBackOrders = requisitions.filter(r => backOrderLocations.includes(r.Location || ""));

    const inventoryValue = inventoryForBackOrders.reduce((sum, value) => sum + (value.InventoryValue || 0), 0);
    const purchaseOrderValue = inventoryForBackOrders.reduce((sum, value) => sum + (value.PurchaseOrderValue || 0), 0);
    const salesOrderValue = inventoryForBackOrders.reduce((sum, value) => sum + (value.SalesOrderValue || 0), 0);
    const draftOrdersValue = requisitionsForBackOrders.reduce((sum, value) => sum + (value.SubTotal || 0), 0);

    const shouldDisplay = (inventoryValue + purchaseOrderValue + salesOrderValue > 0) || (draftOrdersValue > 0);

    return {
      inventoryValue,
      draftOrdersValue,
      purchaseOrderValue,
      salesOrderValue,
      shouldDisplay
    };
  }, [inventoryValues, requisitions]);

  const totals = React.useMemo(() => ({
    inventory: inventoryValues.reduce((sum, value) => sum + (value.InventoryValue || 0), 0),
    draftOrders: requisitions.reduce((sum, value) => sum + (value.SubTotal || 0), 0),
    purchaseOrders: inventoryValues.reduce((sum, value) => sum + (value.PurchaseOrderValue || 0), 0),
    salesOrders: inventoryValues.reduce((sum, value) => sum + (value.SalesOrderValue || 0), 0)
  }), [inventoryValues, requisitions]);


  if (isLoading) {
    return <LoadingComponent text="Loading warehouses..." />;
  }

  return (
    <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-[20px] font-medium text-slate-900">Warehouse values</h3>
          <p className="text-[15px] text-slate-600">
            Inventory and open document totals by warehouse location.
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-14 hover:bg-gray-50">
              <TableHead className="w-10" aria-label="Primary warehouse" />
              <TableHead className="min-w-[220px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Location
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Inventory
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Draft orders
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                POs
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                SOs
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouseDisplayData.map((data) => (
              <TableRow
                key={data.warehouse.WarehouseId}
                className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
              >
                <TableCell className="text-slate-500">
                  {data.isPrimary && (
                    <Star
                      className="h-4 w-4 text-[var(--ops-accent)] fill-[var(--ops-accent)]"
                      aria-label="Primary warehouse"
                    />
                  )}
                </TableCell>
                <TableCell className="font-medium text-slate-900">
                  {data.isPrimary ? <strong>{data.warehouse.Name}</strong> : data.warehouse.Name}
                </TableCell>
                <TableCell className="text-right font-medium text-slate-900">
                  <FormattedNumber value={data.inventoryValue} {...CurrencyUtils.format} />
                </TableCell>
                <TableCell className="text-right text-slate-700">
                  <FormattedNumber value={data.draftOrdersValue} {...CurrencyUtils.format} />
                </TableCell>
                <TableCell className="text-right text-slate-700">
                  <FormattedNumber value={data.purchaseOrderValue} {...CurrencyUtils.format} />
                </TableCell>
                <TableCell className="text-right text-slate-700">
                  <FormattedNumber value={data.salesOrderValue} {...CurrencyUtils.format} />
                </TableCell>
              </TableRow>
            ))}

            {backOrdersData.shouldDisplay && (
              <TableRow className="h-14 border-b border-gray-100 hover:bg-gray-50/60">
                <TableCell />
                <TableCell className="font-medium text-slate-900">Back Orders</TableCell>
                <TableCell className="text-right font-medium text-slate-900">
                  <FormattedNumber value={backOrdersData.inventoryValue} {...CurrencyUtils.format} />
                </TableCell>
                <TableCell className="text-right text-slate-700">
                  <FormattedNumber value={backOrdersData.draftOrdersValue} {...CurrencyUtils.format} />
                </TableCell>
                <TableCell className="text-right text-slate-700">
                  <FormattedNumber value={backOrdersData.purchaseOrderValue} {...CurrencyUtils.format} />
                </TableCell>
                <TableCell className="text-right text-slate-700">
                  <FormattedNumber value={backOrdersData.salesOrderValue} {...CurrencyUtils.format} />
                </TableCell>
              </TableRow>
            )}

            <TableRow className="h-14 bg-gray-50 font-semibold">
              <TableCell />
              <TableCell className="text-slate-900">Total</TableCell>
              <TableCell className="text-right text-slate-900">
                <FormattedNumber value={totals.inventory} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell className="text-right text-slate-900">
                <FormattedNumber value={totals.draftOrders} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell className="text-right text-slate-900">
                <FormattedNumber value={totals.purchaseOrders} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell className="text-right text-slate-900">
                <FormattedNumber value={totals.salesOrders} {...CurrencyUtils.format} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export default VendorValues;
